import type { DeviceAddress } from '@/hooks/useDeviceAddresses';

/** One line of the device page's address list. */
export interface AddressRow {
  ipAddress: string;
  isPrimary: boolean;
  internal: string | null;
  interfaces: string[];
  vrfs: string[];
  sources: string[];
  lastSeenAt: string;
}

const SOURCE_LABELS: Record<string, string> = {
  snmp: 'SNMP',
  eapi: 'eAPI',
  ssh: 'SSH',
  netconf: 'NETCONF',
  lldp: 'LLDP',
  manual: 'Entered',
};

/** The collector name as a person reads it; an unfamiliar one is shown as sent. */
export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

function unique(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))];
}

function toRow(a: DeviceAddress): AddressRow {
  return {
    ipAddress: a.ipAddress,
    isPrimary: a.isPrimary,
    internal: a.internal,
    interfaces: unique(a.sightings.map((s) => s.interfaceName)),
    vrfs: unique(a.sightings.map((s) => s.vrf)),
    sources: unique(a.sightings.map((s) => sourceLabel(s.source))),
    lastSeenAt: a.lastSeenAt,
  };
}

/** Primary first, then addresses reachable from the network, then numerically (192.168.0.9 before .10). */
function compareRows(a: AddressRow, b: AddressRow): number {
  if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
  if (!a.internal !== !b.internal) return a.internal ? 1 : -1;
  return a.ipAddress.localeCompare(b.ipAddress, undefined, { numeric: true });
}

/** The address list split by family, each sorted for reading. */
export function groupAddresses(addresses: DeviceAddress[]): { ipv4: AddressRow[]; ipv6: AddressRow[] } {
  const rows = addresses.map(toRow);
  const ipv4 = addresses.flatMap((a, i) => (a.family === 'ipv6' ? [] : [rows[i]])).sort(compareRows);
  const ipv6 = addresses.flatMap((a, i) => (a.family === 'ipv6' ? [rows[i]] : [])).sort(compareRows);
  return { ipv4, ipv6 };
}
